const CLIENT_ERROR_MIN = 400;
const CLIENT_ERROR_MAX = 500;

/**
 * An error carrying a message the API intended for the user, taken from a 4xx response body.
 * Server errors (5xx) and unreadable bodies never become an ApiError, so callers can fall back
 * to their own generic wording for those.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const isClientError = (status: number): boolean => status >= CLIENT_ERROR_MIN && status < CLIENT_ERROR_MAX;

const readBodyMessage = async (response: Response): Promise<string | undefined> => {
  try {
    const body: unknown = await response.json();
    const message = (body as { message?: unknown })?.message;
    return typeof message === 'string' && message.trim() !== '' ? message : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Turns a failed fetch Response into an Error. Returns an ApiError with the body's message for a
 * 4xx response that carries one, otherwise a plain Error so the caller shows its generic message.
 */
export const toApiError = async (response: Response, fallbackMessage: string): Promise<Error> => {
  const message = isClientError(response.status) ? await readBodyMessage(response) : undefined;
  return message ? new ApiError(message, response.status) : new Error(fallbackMessage);
};
